import React, { useState, useEffect } from 'react';
import Banner from '../components/common/Banner';
import { apiService } from '../services/api';
import { SecureContent } from '../utils/sanitize';
import { getImageUrl } from '../utils/imageUtils';

const VisionMissionPage = () => {
  const [visionMissionData, setVisionMissionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVisionMissionData();
  }, []);

  const fetchVisionMissionData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getVisionMission();
      setVisionMissionData(response.data);
    } catch (error) {
      console.error('Error fetching vision mission data:', error);
      // Keep default data if API fails
    } finally {
      setLoading(false);
    }
  };

  // Default data for fallback
  const defaultData = {
    bannerTitle: 'Vision & Mission',
    bannerSubtitle: 'Shaping the future through excellence in education and innovation',
    bannerImage: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    visionTitle: 'Our Vision',
    visionContent: '<p>To be a premier institute of technology that empowers students to become innovative leaders and ethical professionals who contribute meaningfully to society and the global economy.</p><p>We envision FiT Engineering College as a center of excellence that bridges the gap between academic knowledge and industry requirements, fostering a culture of research, innovation, and entrepreneurship.</p><p>Our vision extends beyond traditional education to create well-rounded individuals who possess not only technical expertise but also strong ethical values, leadership qualities, and a commitment to sustainable development.</p>',
    missionTitle: 'Our Mission',
    missionContent: '<p>To provide world-class technical education that combines theoretical knowledge with practical application, preparing students for successful careers in an ever-evolving technological landscape.</p><p>We are committed to:</p><ul><li>Delivering innovative and industry-relevant curriculum that meets global standards</li><li>Fostering research and development activities that contribute to technological advancement</li><li>Creating an inclusive learning environment that promotes creativity and critical thinking</li><li>Building strong partnerships with industry leaders and academic institutions worldwide</li><li>Developing responsible citizens who contribute to society\'s welfare and sustainable development</li><li>Encouraging entrepreneurship and innovation among students and faculty</li></ul>',
    coreValuesTitle: 'Our Core Values',
    coreValues: [
      {
        icon: 'fas fa-star',
        title: 'Excellence',
        description: 'Striving for the highest standards in everything we do'
      },
      {
        icon: 'fas fa-lightbulb',
        title: 'Innovation',
        description: 'Encouraging creative thinking and pioneering solutions'
      },
      {
        icon: 'fas fa-heart',
        title: 'Integrity',
        description: 'Upholding honesty and ethical practices in all endeavors'
      },
      {
        icon: 'fas fa-users',
        title: 'Collaboration',
        description: 'Working together to achieve common goals and success'
      }
    ]
  };

  // Use dynamic data if available, otherwise use defaults
  const currentData = visionMissionData || defaultData;

  return (
    <div className="bg-white">
      {/* Hero Banner */}
      <Banner 
        title={currentData.bannerTitle || defaultData.bannerTitle}
        subtitle={currentData.bannerSubtitle || defaultData.bannerSubtitle}
        imageSrc={currentData.bannerImage ? getImageUrl(currentData.bannerImage) : defaultData.bannerImage}
      />
      
      {/* Main Content */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          
          {/* Vision Section */}
          <div className="bg-white rounded-xl shadow-soft p-12 md:p-16 mb-12">
            <div className="grid md:grid-cols-3 gap-12">
              {/* Image Placeholder */}
              <div className="md:col-span-1">
                {currentData.visionImage ? (
                  <img 
                    src={getImageUrl(currentData.visionImage)} 
                    alt="Vision"
                    className="w-full aspect-[4/3] object-cover rounded-lg"
                  />
                ) : (
                  <div className="bg-white rounded-lg aspect-[4/3] flex items-center justify-center mb-6 border-2 border-gray-200">
                    <div className="text-center">
                      <i className="fas fa-eye text-6xl text-crimson mb-3"></i>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Vision Image</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Vision Content */}
              <div className="md:col-span-2">
                <h2 className="text-3xl md:text-4xl font-sans font-bold text-crimson mb-6">
                  {currentData.visionTitle || defaultData.visionTitle}
                </h2>
                <SecureContent 
                  content={currentData.visionContent || defaultData.visionContent}
                  className="text-gray-600 leading-relaxed rich-text-content"
                />
              </div>
            </div>
          </div>

          {/* Mission Section */}
          <div className="bg-white rounded-xl shadow-soft p-12 md:p-16 mb-12">
            <div className="grid md:grid-cols-3 gap-12">
              {/* Mission Content */}
              <div className="md:col-span-2">
                <h2 className="text-3xl md:text-4xl font-sans font-bold text-crimson mb-6">
                  {currentData.missionTitle || defaultData.missionTitle}
                </h2>
                <SecureContent 
                  content={currentData.missionContent || defaultData.missionContent}
                  className="text-gray-600 leading-relaxed rich-text-content"
                />
              </div>
              
              {/* Image Placeholder */}
              <div className="md:col-span-1">
                {currentData.missionImage ? (
                  <img 
                    src={getImageUrl(currentData.missionImage)} 
                    alt="Mission"
                    className="w-full aspect-[4/3] object-cover rounded-lg"
                  />
                ) : (
                  <div className="bg-white rounded-lg aspect-[4/3] flex items-center justify-center mb-6 border-2 border-gray-200">
                    <div className="text-center">
                      <i className="fas fa-bullseye text-6xl text-crimson mb-3"></i>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Mission Image</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Core Values Section */}
          <div className="bg-white rounded-xl shadow-soft p-12 md:p-16">
            <h2 className="text-3xl md:text-4xl font-sans font-bold text-crimson mb-8 text-center">
              {currentData.coreValuesTitle || defaultData.coreValuesTitle}
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {(currentData.coreValues || defaultData.coreValues).map((value, index) => (
                <div key={index} className="text-center">
                  <div className="bg-crimson/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                    <i className={`${value.icon} text-3xl text-crimson`}></i>
                  </div>
                  <h3 className="font-bold text-lg text-slate mb-2">{value.title}</h3>
                  <p className="text-sm text-gray-600">{value.description}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default VisionMissionPage;